import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { hasFeatureAccess } from '../../utils/featureLock';
import { PROJECT_STATUS, getProjectStatusLabel } from '../../utils/projectStatus';
import { formatPageTitle } from '../../utils/pageTitles';
import '../Documents/commonstyle.css';
import '../tasks/tasklist.css';
import './projectDashboard.css';

const SECTION_ORDER = ['today', 'future', 'overdue'];

function startOfDay(value) {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isActiveProject(project) {
  const status = Number(project?.status);
  return status !== PROJECT_STATUS.COMPLETED && status !== PROJECT_STATUS.DELETED;
}

function isProjectOverdue(project) {
  if (!project?.end_date) return false;
  const end = startOfDay(project.end_date);
  if (Number.isNaN(end.getTime())) return false;
  return end < startOfDay(new Date());
}

function isTaskOverdue(task) {
  if (Number(task.status) === 3 || !task.due_date) return false;
  const due = startOfDay(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  return due < startOfDay(new Date());
}

/** Soonest end first so overdue projects surface before later ones. */
function projectSortKey(project) {
  if (!project?.end_date) return Number.MAX_SAFE_INTEGER;
  const end = startOfDay(project.end_date).getTime();
  return Number.isNaN(end) ? Number.MAX_SAFE_INTEGER : end;
}

function formatCompactDateRange(start, end) {
  const fmt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const startLabel = fmt(start);
  const endLabel = fmt(end);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel || '—';
}

function getInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getAvatarTone(seed = '') {
  const str = String(seed || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash % 8;
}

function getPriorityBadgeClass(priority) {
  switch (String(priority || '').toLowerCase()) {
    case 'high':
      return 'priority-badge priority-high';
    case 'medium':
      return 'priority-badge priority-medium';
    case 'low':
      return 'priority-badge priority-low';
    default:
      return 'priority-badge priority-low';
  }
}

function getTaskStatusBadgeClass(status) {
  switch (Number(status)) {
    case 0:
      return 'status-badge status-pending';
    case 1:
      return 'status-badge status-ongoing';
    case 2:
      return 'status-badge status-pending';
    case 3:
      return 'status-badge status-completed';
    case 4:
      return 'status-badge status-pending';
    case 5:
      return 'status-badge status-ongoing';
    default:
      return 'status-badge status-not-started';
  }
}

function getProjectStatusBadgeClass(status) {
  switch (Number(status)) {
    case PROJECT_STATUS.PENDING:
      return 'status-badge status-pending';
    case PROJECT_STATUS.IN_PROGRESS:
      return 'status-badge status-ongoing';
    case PROJECT_STATUS.MARK_AS_COMPLETED:
      return 'status-badge status-ongoing';
    case PROJECT_STATUS.COMPLETED:
      return 'status-badge status-completed';
    default:
      return 'status-badge status-not-started';
  }
}

function taskStatusLabel(status) {
  switch (Number(status)) {
    case 0:
      return 'Pending';
    case 1:
      return 'In Progress';
    case 2:
      return 'Blocked';
    case 3:
      return 'Completed';
    case 4:
      return 'Paused';
    case 5:
      return 'Partial';
    default:
      return 'Unknown';
  }
}

function mapTaskRow(task) {
  return {
    id: task.id,
    title: task.title || 'Untitled task',
    status: task.status,
    priority: task.priority || 'Low',
    start_date: task.start_date,
    due_date: task.due_date,
    project_id: task.project_id,
    project_name: task.project?.name || task.Project?.name || task.project_name || null,
  };
}

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const [featureLoading, setFeatureLoading] = useState(true);
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = formatPageTitle('Project Dashboard');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const access = await hasFeatureAccess('project');
        if (!cancelled) setHasProjectAccess(access);
      } catch {
        if (!cancelled) setHasProjectAccess(false);
      } finally {
        if (!cancelled) setFeatureLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const corporateId = userData?.corporateId;

      const [projectsRes, tasksRes] = await Promise.allSettled([
        axiosInstance.get('/project/list', {
          params: {
            page: 1,
            limit: 200,
            ...(corporateId != null ? { CorporateId: parseInt(corporateId, 10) } : {}),
          },
        }),
        axiosInstance.get('/task/list-by-sections', {
          params: {
            page: 1,
            limit: 20,
            only_project_tasks: true,
            ...(corporateId != null ? { CorporateId: parseInt(corporateId, 10) } : {}),
          },
        }),
      ]);

      if (projectsRes.status === 'fulfilled') {
        const payload = projectsRes.value?.data;
        const list =
          payload?.data ||
          payload?.projects ||
          (Array.isArray(payload) ? payload : []);
        const active = (Array.isArray(list) ? list : [])
          .filter(isActiveProject)
          .sort((a, b) => {
            const byEnd = projectSortKey(a) - projectSortKey(b);
            if (byEnd !== 0) return byEnd;
            const aStart = a.start_date ? startOfDay(a.start_date).getTime() : 0;
            const bStart = b.start_date ? startOfDay(b.start_date).getTime() : 0;
            return bStart - aStart;
          })
          .slice(0, 3);
        setProjects(active);
      } else {
        setProjects([]);
      }

      if (tasksRes.status === 'fulfilled') {
        const sections = tasksRes.value?.data?.sections || {};
        const pending = [];
        SECTION_ORDER.forEach((key) => {
          const rows = sections[key]?.tasks || [];
          rows.forEach((task) => {
            if (Number(task.status) === 3) return;
            pending.push(mapTaskRow(task));
          });
        });
        setTasks(pending.slice(0, 10));
      } else {
        setTasks([]);
      }
    } catch (err) {
      console.error('Project dashboard load failed', err);
      setError(err?.message || 'Failed to load project dashboard');
      setProjects([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!featureLoading && hasProjectAccess) {
      loadDashboard();
    }
  }, [featureLoading, hasProjectAccess, loadDashboard]);

  const projectCards = useMemo(() => projects, [projects]);

  if (featureLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!hasProjectAccess) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div className="card">
          <div className="card-body text-center">
            <div className="feature-locked">
              <i
                className="fas fa-lock"
                style={{ fontSize: '3rem', marginBottom: '1rem', color: '#c42126' }}
                aria-hidden="true"
              />
              <h4 className="text-muted mb-3">Project Management Not Available</h4>
              <p className="text-muted mb-4">
                Project management is not available in your current subscription plan.
                Upgrade your plan to access this feature.
              </p>
              <button
                type="button"
                className="btn doc-form-submit-btn"
                onClick={() => navigate('/user-subscription')}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-dashboard task-list-container">
      <div className="project-dashboard__header">
        <div className="project-dashboard__actions">
          <Link to="/task/projectlist" className="project-dashboard__link-btn">
            All projects
          </Link>
          <Link to="/task/projecttasklist" className="project-dashboard__link-btn is-primary">
            Project tasks
          </Link>
        </div>
      </div>

      {loading ? <div className="project-dashboard__loading">Loading dashboard…</div> : null}
      {error ? <div className="project-dashboard__error">{error}</div> : null}

      <section className="project-dashboard__section">
        <div className="project-dashboard__section-head">
          <h2>Top Active Projects</h2>
          <span>Your top open projects — overdue ones are included too</span>
        </div>
        {projectCards.length ? (
          <div className="project-dashboard__project-grid">
            {projectCards.map((project, index) => {
              const overdue = isProjectOverdue(project);
              const ownerName = project?.owner?.full_name || '';
              const startLabel = formatCompactDateRange(project.start_date, null);
              const endLabel = formatCompactDateRange(null, project.end_date);
              const priorityLabel = (project.priority || 'Low').toString();
              return (
                <Link
                  key={project.id}
                  to={`/task/projectdetails/${project.id}`}
                  className={`pd-project-card${overdue ? ' is-overdue' : ''}`}
                >
                  <div className="pd-project-card__accent" aria-hidden="true" />
                  <div className="pd-project-card__top">
                    <div className="pd-project-card__icon" aria-hidden="true">
                      <i className="fas fa-briefcase" />
                    </div>
                    <div className="pd-project-card__chips">
                      {overdue ? <span className="pd-project-card__overdue">Overdue</span> : null}
                      <span className={getProjectStatusBadgeClass(project.status)}>
                        {getProjectStatusLabel(project.status)}
                      </span>
                    </div>
                  </div>
                  <div className="pd-project-card__body">
                    <span className="pd-project-card__id">#{project.id}</span>
                    <h3 className="pd-project-card__title" title={project.name}>
                      {project.name || 'Untitled project'}
                    </h3>
                    <div className="pd-project-card__meta">
                      <div className="pd-project-card__meta-item">
                        <span className="pd-project-card__meta-label">Start</span>
                        <span className="pd-project-card__meta-value">{startLabel}</span>
                      </div>
                      <div className="pd-project-card__meta-item">
                        <span className="pd-project-card__meta-label">End</span>
                        <span
                          className={`pd-project-card__meta-value${
                            overdue ? ' is-overdue' : ''
                          }`}
                        >
                          {endLabel}
                        </span>
                      </div>
                      <div className="pd-project-card__meta-item">
                        <span className="pd-project-card__meta-label">Priority</span>
                        <span className={getPriorityBadgeClass(priorityLabel)}>{priorityLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className="pd-project-card__footer">
                    <div className="pd-project-card__owner">
                      {ownerName ? (
                        <>
                          <span
                            className={`task-assignee-avatar task-assignee-avatar--tone-${getAvatarTone(
                              project?.owner?.id || ownerName
                            )}`}
                            title={ownerName}
                          >
                            {getInitials(ownerName)}
                          </span>
                          <span className="pd-project-card__owner-name" title={ownerName}>
                            {ownerName}
                          </span>
                        </>
                      ) : (
                        <span className="pd-project-card__owner-name is-muted">No owner</span>
                      )}
                    </div>
                    <span className="pd-project-card__rank" aria-hidden="true">
                      {index + 1}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="project-dashboard__empty">
              No active projects right now.{' '}
              <Link to="/task/addproject">Create a project</Link>
            </div>
          )
        )}
      </section>

      <section className="project-dashboard__section">
        <div className="project-dashboard__section-head project-dashboard__section-head--row">
          <div>
            <h2>Pending Project Tasks</h2>
            <span>The next 10 tasks waiting to be finished</span>
          </div>
          {tasks.length > 0 ? (
            <Link to="/task/projecttasklist" className="project-dashboard__inline-link">
              View all
            </Link>
          ) : null}
        </div>
        {tasks.length ? (
          <div className="row row-list">
            {tasks.map((task) => {
              const overdue = isTaskOverdue(task);
              const dateRange = formatCompactDateRange(task.start_date, task.due_date);
              const priorityLabel = (task.priority || 'Low').toString();
              const projectLabel =
                task.project_name || (task.project_id ? `Project #${task.project_id}` : '');
              return (
                <div className="col-lg-4 col-md-6 col-sm-12 mb-2" key={task.id}>
                  <Link
                    to={`/task/taskdetails/${task.id}`}
                    className={`task-card task-card--compact project-dashboard__card-link${
                      overdue ? ' is-overdue' : ''
                    }`}
                  >
                    <div className="task-card-header">
                      <div className="task-card-header-main">
                        <div className="task-id-row">
                          <span className="task-id">#{task.id}</span>
                          <span className={getTaskStatusBadgeClass(task.status)}>
                            {taskStatusLabel(task.status)}
                          </span>
                        </div>
                        <h6 className="task-title" title={task.title}>
                          {task.title}
                        </h6>
                        {projectLabel ? (
                          <div className="project-dashboard__card-project" title={projectLabel}>
                            <i className="fas fa-briefcase" aria-hidden="true" />
                            <span>{projectLabel}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="task-card-footer">
                      <div className="task-card-footer-left">
                        <span
                          className={`task-date-range${overdue ? ' is-overdue' : ''}`}
                          title={dateRange}
                        >
                          <i className="far fa-calendar" aria-hidden="true" />
                          {dateRange}
                          {overdue ? (
                            <span className="overdue-indicator" title="This task is overdue">
                              <i className="fas fa-exclamation-circle" aria-hidden="true" />
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <span className={getPriorityBadgeClass(priorityLabel)}>{priorityLabel}</span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="project-dashboard__empty">
              No pending project tasks.{' '}
              <Link to="/task/projecttasklist">Browse project tasks</Link>
            </div>
          )
        )}
      </section>

      <BackToTop />
      <InternalFooter />
    </div>
  );
};

export default ProjectDashboard;
